// ── Portfolio Performance ─────────────────────────────────────────────────
//
// What each loan was expected to return when it was booked, what it is
// expected to return now, and — for the ones that moved — which payment moved.
//
// THE PAGE READS A SNAPSHOT. IT NEVER SOLVES ANYTHING.
//
//   Every rate here was solved once, by runPerformance, from cash flows that
//   were frozen at the same instant and stored beside it. Nothing on this page
//   recomputes an IRR, re-sums a flow, or derives a rate from other rates. If
//   it did, the page could disagree with the run that produced it, and the
//   reason for snapshotting at all would be gone.
//
//   That is also the answer to the live-IRR question. Future cash flows move
//   every time a payment lands or a draw funds, and an IRR that recalculated
//   itself would move with them — silently, between two people looking at it.
//   Here a number changes when someone runs a recalculation and PUBLISHES it,
//   and both acts are recorded with who did them.
//
// DRAFT AND PUBLISHED
//
//   published  the number of record. What the page shows when nobody asked for
//              anything in particular, and the only run an export should quote.
//   draft      solved, reviewed by nobody yet. Reachable only by picking it
//              from the run list, and banner-marked the whole time it is up.
//   running    still solving; the page polls and says so.
//   failed     did not finish. The message says why.
//
//   Exactly one run is published at a time. "The number of record" stops
//   meaning anything the moment there are two of them.
//
// TWO LEGS, ONE QUESTION
//
//   original   ONE outflow — the first funding amount — against the
//              origscheduled* snapshot. No later funding, no draw, no received
//              payment. What the deal promised on the day it was written.
//   current    every funding and draw, actuals through the as-of date, then the
//              remaining schedule and forecast losses. What it is now expected
//              to return.
//
//   The two legs therefore invest different money, deliberately. The original
//   leg cannot see a draw taken two years later because nothing written at
//   origination predicted it — which is why a revolver's divergence is the
//   least reliable figure here, and a term loan's the most.
//
//   Divergence is current − original, in basis points. The table ranks by the
//   absolute value of it, so the loans worth a conversation are at the top
//   without anyone sorting a column. That ranking is the first half of "why
//   did the IRR move"; the per-loan drill is the second, and it is the
//   concrete half — not a category, the actual dated flow that differs.
//
// ACCOUNT RATES ARE POOLED, NOT AVERAGED
//
//   An account row is an IRR solved on that account's combined flows. It is
//   not the mean of its loans' rates, and it will not equal one — a $20mm loan
//   at 9% and a $200k loan at 30% pool to something near 9%, and averaging
//   them would print 19.5%. Same for the portfolio row. The run does this
//   solve; the page only displays it, which is exactly why it must not be
//   tempted to compute a roll-up of its own.
//
// A RATE THAT DOES NOT EXIST IS NOT ZERO
//
//   A loan whose flows never change sign has no IRR — money went out and
//   nothing came back, or nothing has gone out yet. The solver says so and the
//   run stores the reason. Those rows show the reason, never a blank cell and
//   never a dash that reads as zero. One flag travels with a rate that does
//   exist but should be read carefully:
//
//     short       under 90 days of history. Annualising three weeks is
//                 arithmetically fine and describes nothing.
//
// WHY "AMBIGUOUS" IS NOT SHOWN, THOUGH THE RUN STILL RECORDS IT
//
//   More than one sign change in a loan's flows means Descartes permits more
//   than one valid rate, and the one on screen is whichever the solver reached.
//   That is true, and on this book it is true of very nearly every loan: a
//   revolver changes sign at every re-draw, and this is a revolver book.
//
//   A warning that fires on almost every row is not a warning. It trains the
//   reader to scroll past the column that also carries "no outflow to measure
//   against" and "solved but did not converge" — the two that are rare and do
//   mean something. Removed from the page for that reason, not because it
//   stopped being true.
//
//   `ambiguous` and `signchanges` are still written to PerformanceIRR on every
//   run, so the question can be asked in SQL when somebody wants it. Nothing
//   was dropped from the data; one note was dropped from the screen.

import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE,
         SURFACE_MUTED, HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, FilterSelect, Btn, SearchBar, Modal, useEscape, TabBtn } from "../../../shared/ui";
import { MonthEndNotice, ReforecastWarning, inMonthEndLock,
         SafeBtn, ProceedBtn, CancelBtn } from "../../../shared/monthEndLock";
import { fmtDate, exportCSV } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";
import { MarkPanel } from "./PortfolioMarkPanel";

const num = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };

// A rate is a decimal. Percent everywhere on the page, two places, because a
// third is precision an IRR solved off a forward curve does not have.
const pct = r => (r === null || r === undefined || !isFinite(r))
  ? "—" : (r * 100).toFixed(2) + "%";

// Divergence is quoted in basis points, not percentage points. "Down 180bps"
// is unambiguous; "down 1.8%" gets read as a relative change by half the room.
const bps = d => (d === null || d === undefined || !isFinite(d))
  ? "—" : (d >= 0 ? "+" : "−") + Math.abs(d * 10000).toFixed(0);

const money0 = n => (n === null || n === undefined) ? "—"
  : (n < 0 ? "-$" : "$") + Math.round(Math.abs(num(n))).toLocaleString("en-US");

const mult = m => (m === null || m === undefined || !isFinite(m))
  ? "—" : Number(m).toFixed(2) + "x";

// ── WHAT THE NOTES COLUMN CARRIES, AND WHAT IT NO LONGER DOES ──────────────
//
// It used to generate qualifiers: "more than one sign change", "only 34 days —
// too short to annualise". Both were true and neither was useful. On a revolver
// book the first fired on nearly every row, and once a column is mostly noise
// nobody reads the entries that are not.
//
// What is left is only what a person cannot work out from the row itself:
//
//   the solver's reason, when there is NO rate. Not a qualifier — it is the
//   explanation for the em-dash in the rate column, and without it "—" reads
//   as zero. Money has not gone out yet, or the solve failed; those are
//   different facts and the row must be able to say which.
//
//   an administrator's own note, written against the loan. Persists across
//   runs, because "this revolver re-drew in March" is a fact about the loan
//   and not about the run that happened to surface it.
const notesOf = (c, extra) => {
  const n = [];
  if (!c) return n;
  if (c.reason) n.push(c.reason);
  (c.adminNotes || []).forEach(t => n.push(t));
  if (extra) n.push(extra);
  return n;
};

// ── ACCOUNT-ROW DIMENSIONS COME FROM ITS LOANS ─────────────────────────────
// An account-scope IRR row has loanid NULL, so the run's OUTER APPLY onto
// Terms matches nothing and facility, ABL/CF and maturity all come back empty —
// which is why every one of those columns rendered as a dash while State, which
// joins on the account, did not. They are rolled up here instead: the value
// when the account's loans agree, "Mixed" when they do not.
const rollup = (loans, f) => {
  const vals = [...new Set(loans.map(l => l[f])
    .filter(v => v !== null && v !== undefined && String(v).trim() !== ""))];
  return vals.length === 1 ? vals[0] : vals.length > 1 ? "Mixed" : null;
};
// The relationship's final maturity, not the first one to come due — an
// account row describes the whole exposure.
const latest = (loans, f) => loans.map(l => l[f]).filter(Boolean).sort().slice(-1)[0] || null;

// ── SORTING THE MOVERS TABLE ───────────────────────────────────────────────
//
// Every column sorts, but the table's default is not a column sort at all: it
// is |change| descending, which is what makes it "biggest movers" rather than
// an alphabetical list that happens to contain rates. That default is its own
// key so a reader can always get back to it, and the card says which of the two
// it is currently in.
//
// The Change column, clicked, sorts SIGNED — a reader who clicks it wants to
// see the worst decliners together, not the largest moves in either direction.
// Those are different questions and the table can answer both.
//
// MISSING VALUES SINK IN BOTH DIRECTIONS. A loan with no rate is not the
// smallest rate, and ascending order must not open with a screen of loans that
// have no number at all.
const SORTS = {
  movers:         { num:  r => (r.diff === null || r.diff === undefined) ? null : Math.abs(r.diff) },
  // THE DEFAULT, and what the Company / Loan header drives.
  //
  // Company name at both levels, with loan number breaking the tie inside an
  // account — which is exactly what that column displays on each kind of row.
  // The previous key read the loan NUMBER on a loan row, so an account whose own
  // IRR had not solved fell back to its first loan's number and landed
  // alphabetically nowhere.
  company:        { text: r => r.companyname },
  facilitytype:   { text: r => r.facilitytype },
  collateraltype: { text: r => r.collateraltype },
  state:          { text: r => r.state },
  // ISO dates sort correctly as text, so no parsing and no timezone to get
  // wrong; a blank maturity sinks like any other missing value.
  maturitydate:   { text: r => r.maturitydate ? String(r.maturitydate).slice(0, 10) : null },
  origRate:       { num:  r => r.origRate },
  currRate:       { num:  r => r.currRate },
  diff:           { num:  r => r.diff },
  moneymultiple:  { num:  r => (r.moneymultiple === null || r.moneymultiple === undefined)
                                 ? null : num(r.moneymultiple) },
  netcash:        { num:  r => r.netcash },
  flowcount:      { num:  r => r.flowcount || null },
};

const cmp = (key, dir) => {
  const s = SORTS[key] || SORTS.movers;
  const sign = dir === "asc" ? 1 : -1;
  const miss = v => v === null || v === undefined || v === "";
  return (a, b) => {
    const va = s.num ? s.num(a || {}) : s.text(a || {});
    const vb = s.num ? s.num(b || {}) : s.text(b || {});
    if (miss(va)) return miss(vb) ? tie(a, b) : 1;   // missing sinks either way
    if (miss(vb)) return -1;
    const d = s.num ? (va - vb) * sign
                    : String(va).localeCompare(String(vb)) * sign;
    return d || tie(a, b);
  };
};

// Every loan of one account shares its company name, so the default sort says
// nothing about the order WITHIN an account. Loan number decides that, and
// ascending regardless of the arrow — the arrow is about companies.
const tie = (a, b) => String((a && a.loannumber) || "")
  .localeCompare(String((b && b.loannumber) || ""));

// Numbers open descending (the largest first is what anyone means by "sort by
// net cash"); text opens ascending.
const defaultDir = key => (SORTS[key] && SORTS[key].text) ? "asc" : "desc";

// ── FLOW CATEGORIES ────────────────────────────────────────────────────────
//
// A flow type carries its component in its last word — Received Principal,
// Scheduled Interest, Original Fee, Participation Other — so the category is
// read off the type rather than kept in a second list that would drift the
// first time a type is added.
//
// EVERY TYPE LANDS IN EXACTLY ONE CATEGORY, including the ones that have no
// single component: Past Due Unpaid, Forecast Charge-Off, Original Scheduled,
// Original Participation and Participation Payment are whole instalments or
// aggregates. They go to "Mixed", named honestly, rather than being quietly
// unreachable — a filter that can drop rows no option can bring back would let
// a reader believe they were looking at everything.
//
// PROJECTED PARTICIPATION IS NOT ONE OF THEM ANY MORE. It used to be written as
// a single "Projected Participation" row, which put the participant offset in
// Mixed on exactly the loans where filtering Principal most needs it — the
// gross would show with no share netted against it. It is now split into
// Participation Principal/Interest/Fee/Other on the projected basis, so each
// component offsets its own. Runs made before that change still carry the lump
// and still land in Mixed, correctly.
const FLOW_CATS = [
  { key:"funding",   label:"Fundings",  is: t => t === "Term Funding" || t === "Draw" },
  { key:"principal", label:"Principal", is: t => /\bPrincipal$/.test(t) },
  { key:"interest",  label:"Interest",  is: t => /\bInterest$/.test(t) },
  { key:"fee",       label:"Fees",      is: t => /\bFee$/.test(t) },
  { key:"other",     label:"Other",     is: t => /\bOther$/.test(t) },
  { key:"mixed",     label:"Mixed",     is: () => true },
];
const catOf = t => (FLOW_CATS.find(c => c.is(String(t || ""))) || {}).key;

const ymOf = d => String(d || "").slice(0, 7);
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = ym => {
  const [y, m] = String(ym).split("-");
  return MONTHS[parseInt(m, 10) - 1] + " " + y;
};

const runLabel = r => {
  if (!r) return "";
  const when = String(r.createdat || "").slice(0, 10);
  return `${fmtDate(r.asofdate)} · ${r.status}${when ? " · run " + fmtDate(when) : ""}`;
};

const STATUS_TONE = {
  published: { bg: B.cyanLight,  bar: B.cyanDark,   fg: INK_PRIMARY },
  draft:     { bg: "#FAEEDA",    bar: B.amberDark,  fg: INK_PRIMARY },
  running:   { bg: "#E6F1FB",    bar: "#185FA5",    fg: INK_PRIMARY },
  failed:    { bg: "#FAECE7",    bar: "#993C1D",    fg: "#993C1D"   },
};

// A row carries both legs, the divergence between them, and every dimension
// the filters need — so filtering, ranking and rendering all read one shape.
function mkRow(rec) {
  const o = rec.original, c = rec.current, m = rec.meta;
  const solved = !!(o && o.solved && c && c.solved);
  return {
    key: rec.key,
    loanid: m.loanid, accountid: m.accountid,
    companyname: m.companyname || "—",
    loannumber: m.loannumber, facilitytype: m.facilitytype,
    collateraltype: m.collateraltype, state: m.state,
    industryname: m.industryname, maturitydate: m.maturitydate,
    accountnumber: m.accountnumber,
    origRate: o && o.solved ? num(o.rate) : null,
    currRate: c && c.solved ? num(c.rate) : null,
    // Only defined when BOTH legs solved. A divergence against a leg that
    // has no rate is not a small change, it is not a change at all, and
    // ranking on a fabricated zero would bury the row.
    diff: solved ? num(c.rate) - num(o.rate) : null,
    solved,
    reason: (c && !c.solved && c.reason) || (o && !o.solved && o.reason) || "",
    // Carried off the run but not displayed — see the header note. Kept on the
    // row so putting it back is one line rather than a re-read of the shape.
    ambiguous: !!((c && c.ambiguous) || (o && o.ambiguous)),
    horizondays: c ? c.horizondays : null,
    moneymultiple: c ? c.moneymultiple : null,
    netcash: c ? num(c.netcash) : 0,
    totalin: c ? num(c.totalin) : 0,
    totalout: c ? num(c.totalout) : 0,
    flowcount: c ? c.flowcount : 0,
    lastdate: c ? c.lastdate : null,
    pvatrate: c ? c.pvatrate : null,
  };
}

// ── MultiFilter ────────────────────────────────────────────────────────────
//
// A toolbar filter that takes more than one value at a time. Local to this page
// rather than added to shared/ui, because shared FilterSelect is a <select> and
// every other page on the platform depends on it staying one.
//
// EVERYTHING IS TICKED TO BEGIN WITH, AND YOU UNTICK WHAT YOU DO NOT WANT.
//
//   So the value this control holds is what is EXCLUDED, not what is selected.
//   That is not a detail of storage — it is the only model in which the two
//   ends of the range are both expressible:
//
//     nothing excluded  every value, the unfiltered view
//     all excluded      nothing, which is a real thing to ask for and a real
//                       thing to see the consequence of
//
//   Held the other way round, with the array meaning "selected", empty has to
//   mean "all" (or an untouched control would show an empty table), and then
//   unticking the last box lands on the same empty array and silently means
//   everything. The reader unticks the final company and the whole book comes
//   back. There is no way to write that as anything but a lie.
//
//   ALL and NONE are both offered in the menu, because with twenty companies
//   ticked, isolating one of them by hand is nineteen clicks.
//
// <details>/<summary> for the native keyboard behaviour, but CONTROLLED, so it
// can be closed by something other than the summary.
//
// It used to be uncontrolled, and the comment here argued that skipping the
// outside-click handler was the cheaper trade because the list sits beside the
// table it filters. That was wrong in use: untick three companies from a list
// of twenty-seven and the menu stays open over the rows you are trying to read,
// with no Done, no Escape and nothing to click but the summary hidden behind it.
// A filter you cannot put away is a filter that covers its own answer.
//
// Three ways out now, because different people reach for different ones:
// clicking anywhere else, pressing Escape, and a Done button in the menu.
const MultiFilter = ({ label, value, onChange, options, allLabel = "All" }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  const off  = new Set(value || []);                 // excluded
  const kept = options.filter(o => !off.has(o.value));

  // Escape, bound only while the menu is open so four of these on one toolbar
  // do not each keep a listener alive for the life of the page.
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Two names fit; past that a count beats a truncated list. "0 of 14" is the
  // one that has to be unmistakable — an empty table is otherwise indis-
  // tinguishable from a run that returned nothing.
  const summary = !off.size ? allLabel
    : !kept.length ? `none of ${options.length}`
    : kept.length <= 2 ? kept.map(o => o.label).join(", ")
    : `${kept.length} of ${options.length}`;

  const toggle = v => {
    const next = new Set(off);
    if (next.has(v)) next.delete(v); else next.add(v);
    // Rebuilt from `options` so the stored order is the canonical one rather
    // than the order boxes happened to be clicked in — two readers who exclude
    // the same three companies then produce the same state.
    onChange(options.map(o => o.value).filter(k => next.has(k)));
  };

  const active = off.size > 0;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13 }}>
      <span style={{ fontSize:11, fontWeight:700, color:INK_MUTED,
                     textTransform:"uppercase", letterSpacing:"0.06em",
                     whiteSpace:"nowrap" }}>{label}</span>
      <details open={open} onToggle={e => setOpen(e.currentTarget.open)}
               style={{ position:"relative" }}>
        <summary style={{ padding:"6px 10px", borderRadius:8, fontSize:13,
                          cursor:"pointer", listStyle:"none", whiteSpace:"nowrap",
                          border:"1px solid " + (active ? B.cyan : HAIRLINE_FIRM),
                          background:SURFACE, color:INK_PRIMARY }}>
          {summary} ▾
        </summary>
        {/* Catches a click anywhere else. A fixed full-screen sheet rather than a
            document listener: the listener has to be torn down on unmount and
            races the summary's own click, which is the half of this that usually
            ships broken. Under the menu, over everything else. */}
        {open && (
          <div onClick={() => setOpen(false)}
               style={{ position:"fixed", inset:0, zIndex:4 }} />
        )}
        <div style={{ position:"absolute", zIndex:5, top:"calc(100% + 4px)", left:0,
                      minWidth:190, maxHeight:320, overflowY:"auto",
                      background:SURFACE, borderRadius:8,
                      border:"1px solid " + HAIRLINE_FIRM,
                      boxShadow:"0 6px 20px rgba(0,0,0,0.12)", padding:"4px 0" }}>
          <div style={{ display:"flex", gap:10, padding:"4px 12px 8px",
                        borderBottom:"1px solid " + HAIRLINE, marginBottom:4 }}>
            <button onClick={() => onChange([])} disabled={!active}
                    style={linkBtn(!active)}>All</button>
            <span style={{ color:HAIRLINE_FIRM }}>|</span>
            <button onClick={() => onChange(options.map(o => o.value))}
                    disabled={!kept.length} style={linkBtn(!kept.length)}>None</button>
            <button onClick={() => setOpen(false)}
                    style={{ ...linkBtn(false), marginLeft:"auto", color:INK_MUTED,
                             textDecoration:"none", fontWeight:600 }}>Done</button>
          </div>
          {options.map(o => (
            <label key={o.value}
                   style={{ display:"flex", alignItems:"center", gap:8,
                            padding:"6px 12px", cursor:"pointer", whiteSpace:"nowrap" }}>
              <input type="checkbox" checked={!off.has(o.value)}
                     onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      </details>
    </div>
  );
};

const linkBtn = disabled => ({
  background:"none", border:"none", padding:0, font:"inherit",
  cursor: disabled ? "default" : "pointer",
  color: disabled ? INK_MUTED : B.cyanDark,
  textDecoration: disabled ? "none" : "underline",
});

export const PortfolioPerformancePage = () => {
  const { api, role } = useApp();

  // ── TWO CAPABILITIES, BECAUSE THE BUTTON DOES TWO THINGS ─────────────────
  //
  // This was one flag — `canRun`, meaning servicer — and the server disagreed
  // with it in both directions at once. Pressing Recalculate as a servicer who
  // is not an admin ran `updateScheduledPayments` (servicer-gated, so it
  // SUCCEEDED and rewrote every future scheduled payment) and then
  // `runPerformance` (admin-gated, so it 403'd). The write landed, the solve
  // did not, and the reader was left with a rebuilt schedule and no run.
  //
  // A button offered to somebody the server will refuse is not a cosmetic
  // problem when the refusal lands halfway through.
  //
  //   canSolve       runPerformance + publishPerformance   -> isAdmin
  //   canReforecast  updateScheduledPayments               -> isServicer
  //
  // Both mirror the endpoint they drive. Neither is a substitute for the
  // server's own check, which still refuses regardless of what is on screen.
  //
  // Being an admin does NOT imply the servicer flag — it is a separate column —
  // so an admin without it is not offered the projection step it would fail on.
  // The month-end projection has its own screen, Update Sched Pmts, and that is
  // untouched: this only governs whether a reforecast can happen as a SIDE
  // EFFECT of solving here.
  const canSolve      = !!(role && role.isAdmin);
  const canReforecast = !!(role && (role.isservicer || role.isServicer));

  const [data, setData] = useState(null);
  const [err, setErr]   = useState("");
  const [busy, setBusy] = useState(true);

  const [runid, setRunid] = useState("");     // "" = whatever is published
  const [working, setWorking] = useState(""); // label of an in-flight action
  const [exporting, setExporting] = useState(""); // "flows" | "loans" while downloading
  // Which view of the run: the IRRs, or the Mark (what the book is worth).
  // Both read the same run — the run picker above switches both.
  const [pane, setPane] = useState("performance");
  const [progress, setProgress] = useState(null); // {done,total} while projecting

  // EACH OF THESE HOLDS WHAT IS EXCLUDED, NOT WHAT IS SELECTED.
  // Empty is the unfiltered view; every option present means nothing is shown.
  // See MultiFilter for why it is the only model that can express both ends.
  const [company, setCompany]   = useState([]);
  const [facility, setFacility] = useState([]);
  const [abl, setAbl]           = useState([]);
  const [usState, setUsState]   = useState([]);
  const [q, setQ]               = useState("");
  const [onlySolved, setOnlySolved] = useState(false);

  const [openAcct, setOpenAcct] = useState({});
  const [drill, setDrill]       = useState(null);   // { accountid, name }
  const [drillLoan, setDrillLoan] = useState("");   // "" = every loan pooled
  const [flows, setFlows]       = useState(null);
  const [flowsBusy, setFlowsBusy] = useState(false);
  const [openMonth, setOpenMonth] = useState({});
  const [sortKey, setSortKey] = useState("company");
  const [sortDir, setSortDir] = useState("asc");
  // Default ON: a run off a stale projection is the quieter, worse failure.
  const [refreshProj, setRefreshProj] = useState(true);
  // What the button will ACTUALLY do. A hidden control's state is still state;
  // this is the value every other line reads, so the label, the chain and the
  // permission can never say three different things.
  const willReforecast = refreshProj && canReforecast;

  // ── THE MONTH-END QUESTION ───────────────────────────────────────────────
  //
  // A reforecast here is a SIDE EFFECT of wanting IRRs. Inside the month-end
  // window that side effect rewrites the month end the servicer has already
  // forecast, invoiced and ACH'd — so the dialog leads with the option that
  // gets the IRRs without touching the schedule at all.
  //
  // Only asked when the rebuild would actually happen. Solving on the schedule
  // as it stands is safe every day of the month and is never interrupted.
  const [lockAsk, setLockAsk] = useState(false);
  const [noteFor, setNoteFor] = useState(null);    // the loan row being annotated
  const askRecalc = () => {
    if (!canSolve || working) return;
    if (willReforecast && inMonthEndLock()) { setLockAsk(true); return; }
    recalc(willReforecast);
  };

  // Which component of the flows the drill is showing. Parent state, beside
  // drillLoan, because it is the same kind of thing: a lens on rows already in
  // hand, not a reason to fetch again.
  const [flowCat, setFlowCat] = useState([]);

  // Separate from `err` on purpose. `err` is cleared by load(), which runs
  // immediately after a solve; a projection warning has to outlive that, since
  // it describes the run being loaded rather than a failure to load it.
  const [projWarn, setProjWarn] = useState("");

  // ── THE SELECTION RATE ───────────────────────────────────────────────────
  // What the filtered set of loans returns, pooled. Not stored, not published,
  // and gone the moment the filters clear.
  const [selIrr, setSelIrr]       = useState(null);
  const [selBusy, setSelBusy]     = useState(false);
  const [selErr, setSelErr]       = useState("");

  // ── load ────────────────────────────────────────────────────────────────
  const load = useCallback((rid, quiet) => {
    if (!quiet) setBusy(true);
    setErr("");
    return api.get("getPerformance", rid ? { runid: rid } : undefined)
      .then(d => { setData(d); setBusy(false); return d; })
      .catch(e => { setErr(e.message); setBusy(false); throw e; });
  }, [api]);

  useEffect(() => { load(runid).catch(() => {}); }, [load, runid]);

  const run  = data && data.run;
  const runs = useMemo(() => (data && data.runs) || [], [data]);
  const published = data && data.published;

  // A run in flight is polled rather than left for the user to refresh. Three
  // seconds is slow enough not to hammer the API and fast enough that a 40
  // second solve feels like it is happening.
  useEffect(() => {
    if (!run || run.status !== "running") return;
    const t = setTimeout(() => { load(run.id, true).catch(() => {}); }, 3000);
    return () => clearTimeout(t);
  }, [run, load]);

  // ── pivot ───────────────────────────────────────────────────────────────
  // One pass over the IRR rows produces every scope keyed by leg. Doing this
  // once here rather than filtering per row in the table is what keeps a
  // 76-loan book from re-scanning the array a few thousand times a render.
  const piv = useMemo(() => {
    const loans = new Map(), accts = new Map();
    let port = { original: null, current: null };
    ((data && data.irr) || []).forEach(r => {
      if (r.scope === "portfolio") { port[r.leg] = r; return; }
      const map = r.scope === "loan" ? loans : accts;
      const key = String(r.scope === "loan" ? r.loanid : r.accountid);
      if (!map.has(key)) map.set(key, { key, original: null, current: null, meta: r });
      map.get(key)[r.leg] = r;
    });
    return { loans, accts, port };
  }, [data]);

  // ── ADMINISTRATORS' NOTES ────────────────────────────────────────────────
  //
  // Stored on the loan in the platform's existing Notes table, with the source
  // "Performance". Not a new table and not a new endpoint: a note about a loan
  // is a note about a loan, and a second store for them would be a second place
  // to look the day somebody asks where a comment went.
  //
  // ON THE LOAN, NOT ON THE RUN, so a note survives the next publish. "This
  // revolver re-drew in March" explains the divergence in every run that
  // follows, and re-typing it each month is how it stops being written at all.
  //
  // ONE REQUEST FOR THE WHOLE RUN, not one per row.
  const [notes, setNotes] = useState({});        // loanid -> [{id, note, createdby, createdat}]
  const loanIdKey = useMemo(
    () => [...piv.loans.values()].map(r => String(r.meta.loanid)).sort().join(","),
    [piv]);

  const loadNotes = useCallback(() => {
    if (!loanIdKey) { setNotes({}); return Promise.resolve(); }
    return api.get("getNotes", { loanids: loanIdKey, source: "Performance" })
      .then(rows => {
        const by = {};
        (rows || []).forEach(n => {
          const k = String(n.loanid);
          (by[k] = by[k] || []).push(n);
        });
        setNotes(by);
      })
      // A note is commentary. Failing to load one must not take the figures
      // down with it, so this reports nothing and leaves the column empty.
      .catch(() => setNotes({}));
  }, [api, loanIdKey]);
  useEffect(() => { loadNotes(); }, [loadNotes]);

  const loanRows = useMemo(
    () => [...piv.loans.values()].map(mkRow).map(r => ({
      ...r,
      noteRows:   notes[String(r.loanid)] || [],
      adminNotes: (notes[String(r.loanid)] || []).map(n => n.note),
    })), [piv, notes]);

  // ── filters ─────────────────────────────────────────────────────────────
  // EVERY LOAN IS REPRESENTED BY EXACTLY ONE OPTION IN EVERY FILTER, blanks
  // included, as "(none)".
  //
  // Without that, unticking is not total: a reader unticks all fourteen states
  // expecting an empty table and still sees the loans with no state recorded,
  // because no box on the menu stands for them. With it, the checkbox list is
  // a complete partition of the book on that dimension and unticking everything
  // really does leave nothing.
  const opts = useMemo(() => {
    const uniq = f => {
      const vals = loanRows.map(f).map(v =>
        (v === null || v === undefined || String(v).trim() === "") ? "" : String(v));
      const named = [...new Set(vals.filter(Boolean))].sort()
        .map(v => ({ value: v, label: v }));
      return vals.some(v => v === "")
        ? [...named, { value: "", label: "(none)" }]
        : named;
    };
    return {
      company:  uniq(r => r.companyname),
      facility: uniq(r => r.facilitytype),
      abl:      uniq(r => r.collateraltype),
      state:    uniq(r => r.state),
    };
  }, [loanRows]);

  const shownLoans = useMemo(() => {
    // Excluded, not selected. A blank value normalises to "" and is matched by
    // the "(none)" option, so every row is reachable by some checkbox.
    const notIn = (excl, get) => {
      const off = new Set(excl);
      return r => {
        const v = get(r);
        return !off.has((v === null || v === undefined || String(v).trim() === "")
          ? "" : String(v));
      };
    };
    let ls = loanRows;
    if (company.length)  ls = ls.filter(notIn(company,  r => r.companyname));
    if (facility.length) ls = ls.filter(notIn(facility, r => r.facilitytype));
    if (abl.length)      ls = ls.filter(notIn(abl,      r => r.collateraltype));
    if (usState.length)  ls = ls.filter(notIn(usState,  r => r.state));
    if (onlySolved) ls = ls.filter(r => r.solved);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      ls = ls.filter(r => [r.companyname, r.loannumber, r.facilitytype,
                           r.state, r.industryname, r.accountnumber]
        .some(v => String(v || "").toLowerCase().includes(s)));
    }
    return ls;
  }, [loanRows, company, facility, abl, usState, onlySolved, q]);

  // Any filter at all, including "Solved only" and the search box — each of
  // them changes which loans are pooled, so each of them changes the answer.
  const filtering = !!(company.length || facility.length || abl.length
                    || usState.length || onlySolved || q.trim());

  // ── SOLVING THE SELECTION ────────────────────────────────────────────────
  //
  // Whenever the filtered set changes, the selected loans are pooled and solved
  // from THIS RUN'S FROZEN FLOWS, server-side, by the same xirr the run used.
  //
  // WHY THIS IS NOT THE LIVE IRR YOU DIDN'T WANT. The flows are written once,
  // when the run freezes, and never rewritten. Re-asking tomorrow returns the
  // same cash on the same dates and therefore the same rate. What moves when a
  // filter moves is which loans are in the pool — not what any of them is
  // worth. Nothing here reads ScheduledPmts, the forward curve, or anything
  // else that changes under a reader.
  //
  // It is still not the number of record, and the row that shows it says so.
  //
  // WHY NOT SOLVE IT IN THE BROWSER. That would be a second implementation of
  // the solver, and the first day the two disagreed by a basis point the page
  // would be arguing with the run it is displaying.
  //
  // DEBOUNCED because "on every change" includes every keystroke in the search
  // box. 350ms is below noticing and above typing.
  //
  // SEQUENCED because responses can land out of order: a slow answer for three
  // companies must not overwrite a fast one for five. Only the newest request
  // is allowed to paint.
  const selSeq = useRef(0);
  const selKey = useMemo(
    () => shownLoans.map(r => String(r.loanid)).sort().join(","),
    [shownLoans]);

  useEffect(() => {
    const rid = run && run.id;
    // Unfiltered, the portfolio row above already IS this number, solved by the
    // run itself. Asking again would spend a query to reproduce it and invite
    // the two to disagree over rounding.
    if (!rid || !filtering) { setSelIrr(null); setSelErr(""); setSelBusy(false); return; }

    const seq = ++selSeq.current;
    setSelBusy(true);
    setSelErr("");
    const t = setTimeout(() => {
      api.post("solveSelection", { runid: rid, loanids: selKey ? selKey.split(",") : [] })
        .then(d => {
          if (seq !== selSeq.current) return;      // a newer request is in flight
          setSelIrr(d); setSelBusy(false);
        })
        .catch(e => {
          if (seq !== selSeq.current) return;
          setSelErr(e.message); setSelIrr(null); setSelBusy(false);
        });
    }, 350);
    return () => clearTimeout(t);
  }, [api, run, filtering, selKey]);

  const rowCmp = useMemo(() => cmp(sortKey, sortDir), [sortKey, sortDir]);

  // ── the ranking ─────────────────────────────────────────────────────────
  // Accounts, each carrying the loans that survived the filter, ordered by how
  // far the account's own rate moved. The account's RATE is the pooled solve
  // from the run — never a roll-up computed here — but its ORDER is by |diff|,
  // which is the whole point of the table.
  //
  // An account whose own rate did not solve still ranks, by the largest move
  // among its loans, so it cannot vanish from the top of a list it belongs at.
  const groups = useMemo(() => {
    const byAcct = new Map();
    shownLoans.forEach(r => {
      const k = String(r.accountid);
      if (!byAcct.has(k)) byAcct.set(k, []);
      byAcct.get(k).push(r);
    });
    const out = [];
    byAcct.forEach((loans, k) => {
      const rec = piv.accts.get(k);
      const base = rec ? mkRow(rec) : null;
      // The account's own columns win where it has them (state); the rest are
      // rolled up from the loans the run actually covered.
      const acct = base ? {
        ...base,
        facilitytype:   base.facilitytype   || rollup(loans, "facilitytype"),
        collateraltype: base.collateraltype || rollup(loans, "collateraltype"),
        state:          base.state          || rollup(loans, "state"),
        maturitydate:   base.maturitydate   || latest(loans, "maturitydate"),
      } : null;
      loans.sort(rowCmp);
      const rank = acct && acct.diff !== null
        ? Math.abs(acct.diff)
        : Math.max(0, ...loans.map(l => Math.abs(l.diff || 0)));
      out.push({
        key: k,
        companyname: (acct && acct.companyname) || loans[0].companyname,
        acct, loans, rank,
        // Every account is collapsed on load, so a loan that did not solve, or
        // one somebody has written a note against, would otherwise be invisible
        // until the right row was opened. The count rides on the account line;
        // the text itself is on the loan, one click away.
        flagged: loans.filter(l => notesOf(l).length > 0).length,
        // The account IRR was solved on ALL of that account's loans. Once a
        // filter removes some of them the rate no longer describes what is on
        // screen, and saying so beats quietly showing a number for a pool the
        // reader cannot see.
        partial: !!(rec && piv.loans.size && loansOfAcct(piv, k) !== loans.length),
      });
    });
    // Accounts order by the same rule as the loans inside them, so a column
    // means one thing on both levels. The default is the exception: it ranks on
    // the account's own move, falling back to its largest loan move when the
    // account rate did not solve — a rule no single column expresses.
    if (sortKey === "movers") {
      const sign = sortDir === "asc" ? 1 : -1;
      out.sort((a, b) => (a.rank - b.rank) * sign);
    } else if (sortKey === "company") {
      // The group carries the name whether or not the account's own IRR solved,
      // so this never falls through to a loan row and never sorts a company by
      // one of its loan numbers.
      const sign = sortDir === "asc" ? 1 : -1;
      out.sort((a, b) => String(a.companyname || "")
        .localeCompare(String(b.companyname || "")) * sign);
    } else {
      out.sort((a, b) => rowCmp(a.acct || a.loans[0], b.acct || b.loans[0]));
    }
    return out;
  }, [shownLoans, piv, rowCmp, sortKey, sortDir]);

  // Clicking a new column adopts its natural direction; clicking the active one
  // reverses. Nothing resets to the default by accident — that is its own link.
  const sortBy = key => {
    if (key === sortKey) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(defaultDir(key)); }
  };

  // ── headline ────────────────────────────────────────────────────────────
  // Straight off the portfolio-scope rows. Not summed, not averaged, not
  // recomputed from what the filters left on screen — this is the book's
  // pooled rate, and it is the same number whatever is filtered above it.
  const port = useMemo(() => {
    const o = piv.port.original, c = piv.port.current;
    return {
      orig: o && o.solved ? num(o.rate) : null,
      curr: c && c.solved ? num(c.rate) : null,
      diff: (o && o.solved && c && c.solved) ? num(c.rate) - num(o.rate) : null,
      mm: c ? c.moneymultiple : null,
      net: c ? num(c.netcash) : null,
      first: c ? c.firstdate : null,
      last: c ? c.lastdate : null,
      pv: c ? c.pvatrate : null,
    };
  }, [piv]);


  // ── actions ─────────────────────────────────────────────────────────────
  //
  // RECALCULATE IS TWO ACTS, IN ORDER.
  //
  //   1. updateScheduledPayments rebuilds every future scheduled payment off
  //      the current balances and the latest forward curve.
  //   2. runPerformance freezes the resulting flows and solves them.
  //
  // The second reads what the first wrote, and it is told which batch it is
  // reading so the run records its own provenance rather than inferring it.
  //
  // The chaining lives here, in the page, and NOT inside runPerformance. That
  // endpoint's entire contract is that it reads a snapshot and writes nothing
  // but its own three tables; if it started rewriting ScheduledPmts, a failed
  // solve could leave the forward schedule half-rebuilt with nothing saying so.
  // Two endpoints that each do one thing stay debuggable — and the projection
  // survives even when the solve afterwards fails.
  const recalc = async (doReforecast) => {
    if (!canSolve || working) return;
    setErr("");
    setProjWarn("");
    try {
      // ── THE PROJECTION REFRESH IS OPTIONAL ───────────────────────────────
      //
      // Chained by default, because solving off a stale schedule is the
      // quieter and worse of the two failures. But rebuilding the forward
      // schedule is a WRITE across every future row of every loan, and there
      // are good reasons to want the solve without it: re-solving after a rule
      // change to see only that change, comparing two runs against one fixed
      // projection, or re-running when the last batch is known good and the
      // rebuild would only add noise.
      //
      // Skipping it is recorded on the run — the batch id is whatever the run
      // finds rather than one it just made — so a run solved off an older
      // projection still says which one.
      let batchId = null;
      if (doReforecast) {
      setWorking("Updating projections");
      setProgress({ done: 0, total: 0 });
      const b = await api.post("updateScheduledPayments", {});
      if (!b || !b.batchId) throw new Error("updateScheduledPayments returned no batchId.");
      batchId = b.batchId;
      setProgress({ done: 0, total: num(b.total) });

      // The batch runs in the background, one loan at a time. Two seconds
      // matches the Update Sched Pmts page, and the deadline is there so a
      // wedged batch surfaces as an error instead of a button that spins
      // until someone reloads.
      const deadline = Date.now() + 20 * 60 * 1000;
      for (;;) {
        if (Date.now() > deadline)
          throw new Error("The projection batch did not finish within 20 minutes. "
            + "Check Update Sched Pmts before solving.");
        await new Promise(r => setTimeout(r, 2000));
        const st = await api.get("getBatchStatus", { batchid: batchId });
        const done = num(st.done) + num(st.error) + num(st.skipped);
        setProgress({ done, total: num(st.total) || num(b.total) });
        if (st.complete) {
          // ── EVERY LOAN FAILED IS NOT A WARNING, IT IS A STOP ──────────────
          //
          // Nothing was rebuilt. Solving now would produce a run off the
          // schedule exactly as it already stood, which is the quiet wrong
          // number this whole manual-trigger design exists to prevent. A
          // projection that throws on every loan is a code fault, not a data
          // condition, so it ends the action rather than colouring it.
          const total = num(st.total) || num(b.total);
          if (num(st.error) > 0 && num(st.error) >= total)
            throw new Error(`The projection failed on all ${total} loan(s), so no future `
              + "row was rebuilt. Nothing was solved — the numbers on screen are from the "
              + "previous run. Check Update Sched Pmts for the error.");

          // Some failed: the rest of the book did project, so the solve goes
          // ahead — but the draft must not be published without knowing which
          // part of it stands on a schedule that was not refreshed.
          //
          // This lives in its own state, not setErr, because setRunid below
          // triggers load(), and load() begins with setErr("") — so the warning
          // was being set and erased in the same tick, every time.
          if (num(st.error) > 0)
            setProjWarn(`${st.error} of ${total} loan(s) failed to project. The run below is `
              + "solved on a partially refreshed schedule — fix them and recalculate "
              + "before publishing.");
          break;
        }
      }

      }

      setWorking("Solving");
      setProgress(null);
      const r = await api.get("runPerformance",
                              batchId ? { batchid: batchId } : undefined);
      setRunid(r.runId);          // the load effect picks it up and polls it
    } catch (e) {
      setErr(e.message);
    } finally {
      setWorking("");
      setProgress(null);
    }
  };

  const publish = (unpublishing) => {
    if (!canSolve || working || !run) return;
    setWorking(unpublishing ? "Unpublishing" : "Publishing");
    api.post("publishPerformance",
             { runid: run.id, action: unpublishing ? "unpublish" : "publish" })
      .then(() => load(run.id, true))
      .catch(e => setErr(e.message))
      .then(() => setWorking(""), () => setWorking(""));
  };

  // ── drill ───────────────────────────────────────────────────────────────
  //
  // THE WHOLE ACCOUNT IS FETCHED, EVEN WHEN ONE LOAN WAS CLICKED.
  //
  // Combined, an account's flows are exactly the series its IRR was solved on,
  // so the default view explains the number on the row above it. Narrowing to a
  // single loan is then a filter over rows already in hand: no spinner between
  // one loan and the next, and no risk of comparing two loans fetched from
  // different reads of a table that a concurrent run could have been writing.
  const openDrill = (g, loanid) => {
    if (!run) return;
    setDrill({ accountid: g.key, name: g.companyname });
    setDrillLoan(loanid ? String(loanid) : "");
    setFlowCat([]);
    setFlows(null); setOpenMonth({}); setFlowsBusy(true);
    api.get("getPerformance", { runid: run.id, accountid: g.key })
      .then(d => { setFlows(d.flows || []); setFlowsBusy(false); })
      .catch(e => { setErr(e.message); setFlowsBusy(false); });
  };

  const csv = () => {
    const rows = [];
    groups.forEach(g => {
      if (g.acct) rows.push({
        level: "Account", company: g.companyname, loan: "",
        facility: "", state: "", maturity: "",
        original: g.acct.origRate, current: g.acct.currRate,
        change_bps: g.acct.diff === null ? "" : (g.acct.diff * 10000).toFixed(0),
        money_multiple: g.acct.moneymultiple, net_cash: g.acct.netcash,
        flows: g.acct.flowcount, note: g.acct.reason,
      });
      g.loans.forEach(l => rows.push({
        level: "Loan", company: g.companyname, loan: l.loannumber || l.loanid,
        facility: l.facilitytype || "", state: l.state || "",
        maturity: l.maturitydate ? String(l.maturitydate).slice(0, 10) : "",
        original: l.origRate, current: l.currRate,
        change_bps: l.diff === null ? "" : (l.diff * 10000).toFixed(0),
        money_multiple: l.moneymultiple, net_cash: l.netcash,
        flows: l.flowcount, note: l.reason || "",
      }));
    });
    exportCSV(rows, ["level","company","loan","facility","state","maturity",
                     "original","current","change_bps","money_multiple",
                     "net_cash","flows","note"],
              "portfolio_performance.csv");
  };

  // ── EXPORTS FOR THE MARK ─────────────────────────────────────────────────
  // The run's data in the two shapes the Mark Proof workbook
  // (doc/Portfolio_Mark_Proof.xlsx) pastes, column for column:
  //   cash flows  every frozen flow, both legs  -> CashFlows sheet, A–H
  //   loans       rating, par, accrued at as-of -> Loans sheet, A–F
  // Fetched on demand rather than held: a run is tens of thousands of flows.
  const exportRun = async what => {
    if (!run || exporting) return;
    setExporting(what);
    try {
      const d = await api.get("getPerformanceExport", { runid: run.id, what });
      const objs = (d.rows || []).map(row =>
        Object.fromEntries(d.columns.map((c, i) => [c, row[i]])));
      exportCSV(objs, d.columns,
        `performance_${what === "flows" ? "cashflows" : "loans_for_mark"}_${d.asofdate || ""}.csv`);
    } catch (e) {
      setErr(e.message || String(e));
    }
    setExporting("");
  };

  if (busy && !data) return <Spinner />;
  if (err && !data)  return <ErrorMsg msg={err} onRetry={() => load(runid)} />;

  const tone = STATUS_TONE[(run && run.status) || "published"] || STATUS_TONE.published;
  const isDraft = !!(run && run.status === "draft");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* THE SAFE ANSWER IS THE PRIMARY BUTTON. "Solve only" gets the IRRs the
          reader came for and writes nothing to the schedule, so it is the first
          thing the hand reaches for; rebuilding anyway is the danger variant and
          says what it does rather than "OK". */}
      {noteFor && (
        <NoteEditor api={api} who={(role && (role.email || role.name || role.id)) || null}
                    row={loanRows.find(r => r.key === noteFor.key) || noteFor}
                    onClose={() => setNoteFor(null)}
                    onSaved={loadNotes} />
      )}

      {lockAsk && (
        <ReforecastWarning
          onClose={() => setLockAsk(false)}
          lede={"Recalculate would rebuild every future scheduled payment before "
              + "solving. You are inside the month-end window."}
          actions={<>
            <SafeBtn onClick={() => { setLockAsk(false); setRefreshProj(false); recalc(false); }}>
              Solve only — leave the schedule alone
            </SafeBtn>
            <ProceedBtn onClick={() => { setLockAsk(false); recalc(true); }}>
              Rebuild the schedule anyway
            </ProceedBtn>
            <CancelBtn onClick={() => setLockAsk(false)} />
          </>} />
      )}

      {/* ── which run, and what may be done to it ───────────────────────── */}
      <Card style={{ padding:"12px 14px" }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"center" }}>
          <FilterSelect label="Run" value={runid} onChange={setRunid} allLabel={
            published ? "Published · " + fmtDate(published.asofdate) : "No published run"}
            options={runs.map(r => ({ value:r.id, label:runLabel(r) }))} />
          {canSolve && (
            <>
              {/* Beside the button, not buried in a menu: it changes what
                  pressing that button WRITES, and the label has to say so
                  while the finger is on its way. */}
              {canReforecast ? (
                <label style={{ display:"inline-flex", alignItems:"center", gap:6,
                                fontSize:12, color:INK_MUTED, whiteSpace:"nowrap" }}
                       title={"On: rebuilds every future scheduled payment off the latest "
                            + "forward curve, then solves. Off: solves the schedule exactly "
                            + "as it stands and writes nothing to it."}>
                  <input type="checkbox" checked={refreshProj} disabled={!!working}
                         onChange={e => setRefreshProj(e.target.checked)} />
                  Refresh projections first
                </label>
              ) : (
                <span style={{ fontSize:12, color:INK_MUTED, whiteSpace:"nowrap" }}
                      title={"Rebuilding the forward schedule needs the servicer flag. "
                           + "This solves the schedule exactly as it stands."}>
                  Solving on the schedule as it stands
                </span>
              )}
              <Btn onClick={askRecalc} disabled={!!working}
                   title={"Rebuilds every future scheduled payment off the latest forward "
                        + "curve, then solves the IRRs from the result and leaves them in draft."}>
                {!working ? (willReforecast ? "Recalculate" : "Solve only")
                  : working === "Updating projections"
                    ? (progress && progress.total
                        ? `Updating projections… ${progress.done}/${progress.total}`
                        : "Updating projections…")
                    : working === "Solving" ? "Solving…" : working + "…"}
              </Btn>
              {isDraft && (
                <Btn variant="primary" onClick={() => publish(false)} disabled={!!working}
                     title="Makes this draft the number of record.">
                  {working === "Publishing" ? "Publishing…" : "Publish this run"}
                </Btn>
              )}
              {run && run.status === "published" && (
                <Btn onClick={() => publish(true)} disabled={!!working}
                     title="Returns it to draft. Nothing is deleted.">
                  {working === "Unpublishing" ? "Unpublishing…" : "Unpublish"}
                </Btn>
              )}
            </>
          )}
          <div style={{ marginLeft:"auto", display:"flex", gap:8, flexWrap:"wrap" }}>
            <Btn onClick={csv} disabled={!groups.length}
                 title="The rows on screen: one line per loan and account.">Export CSV</Btn>
            <Btn onClick={() => exportRun("flows")} disabled={!run || run.status === "running" || !!exporting}
                 title={"Every cash flow in this run, both legs, by loan and date — "
                      + "pastes into the Mark Proof workbook's CashFlows sheet (A–H)."}>
              {exporting === "flows" ? "Exporting…" : "Export cash flows"}
            </Btn>
            <Btn onClick={() => exportRun("loans")} disabled={!run || run.status === "running" || !!exporting}
                 title={"One row per loan: company, risk rating, par and accrued at the run's "
                      + "as-of date — pastes into the Mark Proof workbook's Loans sheet (A–F)."}>
              {exporting === "loans" ? "Exporting…" : "Export loans for mark"}
            </Btn>
          </div>
        </div>
        {canSolve && canReforecast && (
          <MonthEndNotice>
            Solving without the projection refresh is safe today; rebuilding it
            will change that month end.
          </MonthEndNotice>
        )}
      </Card>

      {/* ── the banner ──────────────────────────────────────────────────── */}
      {/* Always up, never dismissible. Which run is on screen is not a detail
          somebody should have to go looking for — every figure below depends
          on it, and a draft looks exactly like a published run otherwise. */}
      {run && (
        <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                      borderRadius:8, background:tone.bg, fontSize:12, lineHeight:1.6,
                      color:tone.fg, borderLeft:"3px solid " + tone.bar }}>
          <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">
            {run.status === "published" ? "✓" : run.status === "failed" ? "⚠" : "●"}
          </span>
          <span>
            <strong>
              {run.status === "published" && "Published — the number of record."}
              {run.status === "draft"     && "Draft — not the number of record."}
              {run.status === "running"   && "Solving…"}
              {run.status === "failed"    && "This run failed."}
            </strong>{" "}
            As of {fmtDate(run.asofdate)}
            {run.forwardcurvedate ? `, forward curve ${fmtDate(run.forwardcurvedate)}` : ""}
            {run.loancount ? ` · ${run.loancount} loans` : ""}
            {run.flowcount ? ` · ${Number(run.flowcount).toLocaleString("en-US")} flows` : ""}
            {run.createdby ? ` · run by ${run.createdby}` : ""}
            {run.publishedby ? ` · published by ${run.publishedby}` : ""}.
            {run.status === "draft" &&
              " Review it here, then publish it if it is right — nothing else reads a draft."}
            {run.status === "running" && " This page is refreshing itself."}
            {run.message ? " " + run.message : ""}
          </span>
        </div>
      )}

      {err && <ErrorMsg msg={err} onRetry={() => load(runid)} />}
      {projWarn && (
        <div style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 6,
                      background: "#fff8e1", border: "1px solid #f0c000",
                      color: "#6b4e00", fontSize: 13 }}>
          <strong>Projection incomplete.</strong> {projWarn}
        </div>
      )}

      {!run && (
        <Card style={{ padding:"28px 16px", textAlign:"center", color:INK_MUTED, fontSize:13 }}>
          No performance run yet.{canSolve
            ? " Recalculate rebuilds the forward schedule and then solves it. The result lands"
              + " in draft, and nothing shows it as the number of record until it is published."
            : " Ask an administrator to run one."}
        </Card>
      )}

      {run && run.status !== "running" && (
        <div style={{ display:"flex", gap:4 }}>
          <TabBtn id="performance" current={pane} label="Performance" onClick={setPane} />
          <TabBtn id="mark"        current={pane} label="Mark"        onClick={setPane} />
        </div>
      )}

      {run && run.status !== "running" && pane === "mark" && (
        <MarkPanel api={api} run={run} canSave={canSolve} />
      )}

      {run && run.status !== "running" && pane === "performance" && (
        <>
          {/* ── headline ────────────────────────────────────────────────── */}
          <div style={{ display:"grid", gap:12,
                        gridTemplateColumns:"repeat(auto-fit, minmax(190px, 1fr))" }}>
            <Stat label="Original IRR" value={pct(port.orig)}
                  note="the schedule as booked" />
            <Stat label="Current IRR" value={pct(port.curr)}
                  note={port.last ? "through " + fmtDate(port.last) : "actuals, then remaining schedule"} />
            <Stat label="Divergence" value={port.diff === null ? "—" : bps(port.diff) + " bps"}
                  note={port.diff === null ? "one leg did not solve"
                        : port.diff >= 0 ? "expected to return more than booked"
                                         : "expected to return less than booked"}
                  tone={port.diff === null ? null : port.diff >= 0 ? "up" : "down"} />
            <Stat label="Money Multiple" value={mult(port.mm)}
                  note={port.net === null ? "—" : money0(port.net) + " net"} />
            <Stat label="Loans in Run" value={String(piv.loans.size)}
                  note={`${groups.length} account${groups.length === 1 ? "" : "s"} in view`} />

          </div>

          {/* ── SELECTION ───────────────────────────────────────────────────
              Only while a filter is on. Unfiltered, the cards above already ARE
              this pool, solved by the run itself — a second copy could only
              agree redundantly or disagree alarmingly.

              Deliberately BELOW the published figures rather than replacing
              them: both are on screen at once, so a screenshot of a filtered
              view cannot be mistaken for the portfolio. */}
          {filtering && (
            <SelectionRow busy={selBusy} err={selErr} d={selIrr}
                          loans={shownLoans.length}
                          accounts={groups.length}
                          status={run.status} />
          )}

          {/* The solver reports the present value of the frozen flows at the
              rate it found. It should round to nothing. A figure that does not
              is the run saying it did not really converge, and that is worth
              more than a rate nobody questioned. */}
          {port.pv !== null && Math.abs(num(port.pv)) > 1 && (
            <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"9px 13px",
                          borderRadius:8, background:SURFACE_MUTED, fontSize:12, lineHeight:1.6,
                          color:INK_PRIMARY, borderLeft:"3px solid " + B.amberDark }}>
              <span style={{ fontSize:13, lineHeight:1.3 }} aria-hidden="true">{"⚠"}</span>
              <span>
                <strong>Portfolio rate did not fully converge.</strong>{" "}
                Discounted at the solved rate the frozen flows come to {money0(port.pv)},
                which should be zero. Treat the portfolio figure as approximate until
                the run is re-solved.
              </span>
            </div>
          )}

          {/* ── filters ─────────────────────────────────────────────────── */}
          <Card style={{ padding:"12px 14px" }}>
            <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"center" }}>
              <MultiFilter label="Company"  value={company}  onChange={setCompany}
                           options={opts.company}  allLabel="All companies" />
              <MultiFilter label="Facility" value={facility} onChange={setFacility}
                           options={opts.facility} allLabel="All facilities" />
              <MultiFilter label="ABL/CF"   value={abl}      onChange={setAbl}
                           options={opts.abl}      allLabel="All types" />
              <MultiFilter label="State"    value={usState}  onChange={setUsState}
                           options={opts.state}    allLabel="All states" />
              <SearchBar value={q} onChange={setQ}
                         placeholder="Search companies, loans, industries..." />
              <label style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12,
                              color:INK_MUTED, whiteSpace:"nowrap" }}>
                <input type="checkbox" checked={onlySolved}
                       onChange={e => setOnlySolved(e.target.checked)} />
                Solved only
              </label>
            </div>
          </Card>

          {/* ── the ranking ─────────────────────────────────────────────── */}
          <Card style={{ padding:0, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px 10px", display:"flex", gap:12,
                          alignItems:"center", flexWrap:"wrap" }}>
              <SectionTitle>
                Biggest movers
                <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8,
                               textTransform:"none", letterSpacing:0 }}>
                  {sortKey === "movers"
                    ? "original vs current, ranked by how far the rate moved"
                    : sortKey === "company"
                      ? "original vs current, by company"
                      : "sorted by column"}
                  {" · "}{shownLoans.length} loan{shownLoans.length === 1 ? "" : "s"}
                  {/* The ranking the card is named for is one click away
                      whatever the table is currently sorted by, and so is the
                      alphabetical default. Whichever is not active is offered. */}
                  {" · "}
                  {sortKey !== "movers" ? (
                    <button onClick={() => { setSortKey("movers"); setSortDir("desc"); }}
                            style={{ background:"none", border:"none", padding:0,
                                     cursor:"pointer", font:"inherit", color:B.cyanDark,
                                     textDecoration:"underline" }}>
                      rank by biggest movers
                    </button>
                  ) : (
                    <button onClick={() => { setSortKey("company"); setSortDir("asc"); }}
                            style={{ background:"none", border:"none", padding:0,
                                     cursor:"pointer", font:"inherit", color:B.cyanDark,
                                     textDecoration:"underline" }}>
                      back to company name
                    </button>
                  )}
                </span>
              </SectionTitle>
            </div>
            <div style={{ overflowX:"auto", maxHeight:"64vh" }}>
              <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12,
                              fontVariantNumeric:"tabular-nums" }}>
                <thead>
                  <tr>
                    {[
                      { k:"company",        label:"Company / Loan", wide:true },
                      { k:"facilitytype",   label:"Facility" },
                      { k:"collateraltype", label:"ABL/CF" },
                      { k:"state",          label:"State" },
                      { k:"maturitydate",   label:"Maturity" },
                      { k:"origRate",  label:"Original IRR", num:true,
                        help:"IRR of the schedule as booked — the first funding against the origscheduled* snapshot." },
                      { k:"currRate",  label:"Current IRR", num:true,
                        help:"IRR of actuals through the as-of date, then the remaining schedule and forecast losses." },
                      { k:"diff",      label:"Change", num:true, bal:true,
                        help:"Current minus original, in basis points. Sorts signed; the default ranking uses the absolute move." },
                      { k:"moneymultiple", label:"Multiple", num:true,
                        help:"Cash in over cash out. Exists even where an IRR does not." },
                      { k:"netcash",   label:"Net Cash", num:true },
                      { k:"flowcount", label:"Flows", num:true },
                    ].map(c => (
                      <Hd key={c.k} sticky num={c.num} bal={c.bal} help={c.help} wide={c.wide}
                          sort={c.k} active={sortKey === c.k} dir={sortDir} onSort={sortBy}>
                        {c.label}
                      </Hd>
                    ))}
                    {/* Notes is free text assembled from several flags; there is
                        no order in it worth offering. */}
                    <Hd sticky>Notes</Hd>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => (
                    <Fragment key={g.key}>
                      <PerfRow bold
                        name={
                          <button onClick={() => setOpenAcct(o => ({ ...o, [g.key]: !o[g.key] }))}
                                  style={{ background:"none", border:"none", padding:0, cursor:"pointer",
                                           font:"inherit", fontWeight:700, color:INK_PRIMARY,
                                           display:"inline-flex", alignItems:"center", gap:6,
                                           textAlign:"left" }}>
                            {/* Every account carries a caret, one loan or ten,
                                so the table loads uniform and each row behaves
                                the way the one above it did. */}
                            <span style={{ fontSize:9, color:INK_MUTED, width:12,
                                           display:"inline-block" }}>
                              {openAcct[g.key] ? "▼" : "▶"}
                            </span>
                            {g.companyname}
                            <span style={{ fontWeight:400, color:INK_MUTED }}>
                              {" "}({g.loans.length})
                            </span>
                          </button>
                        }
                        after={
                          /* Separate from the caret: expanding shows the loans'
                             RATES, this shows the account's pooled CASH. They
                             answer different questions and one control cannot
                             do both. */
                          <button onClick={() => openDrill(g)}
                                  style={{ background:"none", border:"none", padding:0,
                                           marginLeft:8, cursor:"pointer", fontSize:11,
                                           color:B.cyanDark, textDecoration:"underline",
                                           fontWeight:400 }}>
                            flows
                          </button>
                        }
                        extraNote={[
                          g.partial ? "pooled over all loans, not just those in view" : "",
                          g.flagged ? `${g.flagged} loan${g.flagged === 1 ? "" : "s"} with a note — open to see` : "",
                        ].filter(Boolean).join(" · ")}
                        cols={g.acct || {}} />
                      {openAcct[g.key] && g.loans.map(l => (
                        <PerfRow key={l.key} child cols={l}
                          // Loans only. An account row pools several loans, and
                          // a note has to hang on the one it is about.
                          onNote={canSolve ? (() => setNoteFor(l)) : null}
                          name={
                            <button onClick={() => openDrill(g, l.loanid)}
                                    style={{ background:"none", border:"none", padding:0,
                                             cursor:"pointer", font:"inherit", color:B.cyanDark,
                                             textDecoration:"underline", textAlign:"left" }}>
                              {l.loannumber || l.loanid}
                            </button>
                          } />
                      ))}
                    </Fragment>
                  ))}
                  {!groups.length && (
                    <tr><td colSpan={12} style={{ padding:"28px 16px", textAlign:"center",
                                                  color:INK_MUTED, fontSize:13 }}>
                      No loans match these filters.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── the drill ───────────────────────────────────────────────── */}
          {drill && (
            <FlowDetail name={drill.name} flows={flows} busy={flowsBusy}
                        sel={drillLoan} setSel={setDrillLoan}
                        cat={flowCat} setCat={setFlowCat}
                        openMonth={openMonth} setOpenMonth={setOpenMonth}
                        onClose={() => { setDrill(null); setFlows(null);
                                         setDrillLoan(""); setFlowCat([]); }} />
          )}
        </>
      )}
    </div>
  );
};

// How many loans the run covered for one account, regardless of filters. Used
// only to tell whether the account's pooled rate still describes what is shown.
function loansOfAcct(piv, acctKey) {
  let n = 0;
  piv.loans.forEach(rec => { if (String(rec.meta.accountid) === acctKey) n++; });
  return n;
}

// ── the flow drill ─────────────────────────────────────────────────────────
// The concrete half of "why did it move". Both legs by month, side by side,
// with the difference and a running total of it — so the month the two
// schedules parted company is the first row where the running column stops
// being zero. Open a month to see the individual dated flows behind it.
const FlowDetail = ({ name, flows, busy, sel, setSel, cat, setCat,
                      openMonth, setOpenMonth, onClose }) => {
  // Which loans this account's flows cover, in the order they first appear.
  // Built from the flows themselves rather than from the IRR rows, so the
  // picker can only ever offer a loan that actually has cash in this view.
  const loans = useMemo(() => {
    const m = new Map();
    (flows || []).forEach(f => {
      const k = String(f.loanid);
      if (!m.has(k)) m.set(k, { key: k, label: f.loannumber || k });
    });
    return [...m.values()];
  }, [flows]);

  // "" means every loan pooled — which is the series the account IRR was
  // solved on, and therefore the right thing to show first.
  const byLoan = useMemo(
    () => (flows || []).filter(f => !sel || String(f.loanid) === String(sel)),
    [flows, sel]);

  // Only the categories this account actually has, in the fixed order above —
  // the same rule as the loan picker. An option that selects nothing is a
  // question the data cannot answer, and it invites the reader to conclude the
  // account has no fees when what it has is no fee ROWS in this run.
  const cats = useMemo(() => {
    const present = new Set(byLoan.map(f => catOf(f.flowtype)));
    return FLOW_CATS.filter(c => present.has(c.key));
  }, [byLoan]);

  // `cat` holds EXCLUDED components, matching the filters above the table.
  // Empty is everything; every component excluded is nothing, and both are
  // states a reader can reach and see the consequence of.
  const shown = useMemo(() => {
    const off = new Set(cat || []);
    return off.size ? byLoan.filter(f => !off.has(catOf(f.flowtype))) : byLoan;
  }, [byLoan, cat]);

  // What survived, in canonical order, for the line under the header.
  const keptCats = useMemo(() => {
    const off = new Set(cat || []);
    return cats.filter(c => !off.has(c.key));
  }, [cats, cat]);

  const months = useMemo(() => {
    if (!shown) return [];
    const m = new Map();
    shown.forEach(f => {
      const ym = ymOf(f.flowdate);
      if (!m.has(ym)) m.set(ym, { ym, original: 0, current: 0, rows: [] });
      const g = m.get(ym);
      g[f.leg] += num(f.amount);
      g.rows.push(f);
    });
    const out = [...m.values()].sort((a, b) => a.ym < b.ym ? -1 : 1);
    let run = 0;
    out.forEach(g => { g.diff = g.current - g.original; run += g.diff; g.cum = run; });
    out.forEach(g => g.rows.sort((a, b) =>
      (a.flowdate < b.flowdate ? -1 : a.flowdate > b.flowdate ? 1 : 0)
      || (a.leg < b.leg ? -1 : a.leg > b.leg ? 1 : 0)));
    return out;
  }, [shown]);

  const tot = months.reduce((s, g) => ({
    original: s.original + g.original, current: s.current + g.current }),
    { original: 0, current: 0 });

  // Exports what is on screen. An export that quietly included loans the
  // reader had filtered out would not reconcile to the totals above it.
  const csv = () => exportCSV(
    shown.map(f => ({ loan: f.loannumber || f.loanid, leg:f.leg, basis:f.basis,
      date: String(f.flowdate).slice(0, 10), type:f.flowtype,
      amount: num(f.amount), source: f.sourcetable, sourceid: f.sourceid })),
    ["loan","leg","basis","date","type","amount","source","sourceid"],
    "performance_flows.csv");

  return (
    <Card style={{ padding:0, overflow:"hidden" }}>
      <div style={{ padding:"12px 16px 10px", display:"flex", gap:12,
                    alignItems:"center", flexWrap:"wrap" }}>
        <SectionTitle>
          Flows · {name}
          {sel ? " · " + ((loans.find(l => l.key === String(sel)) || {}).label || sel) : ""}
          <span style={{ fontWeight:400, color:INK_MUTED, marginLeft:8,
                         textTransform:"none", letterSpacing:0 }}>
            {sel
              ? "frozen by the run — original against current, by month"
              : `all ${loans.length} loan${loans.length === 1 ? "" : "s"} pooled — `
                + "the same series the account IRR was solved on"}
          </span>
        </SectionTitle>
        <div style={{ display:"flex", gap:8, marginLeft:"auto", alignItems:"center" }}>
          {loans.length > 1 && (
            <FilterSelect label="Loan" value={sel} onChange={setSel}
                          allLabel={`All ${loans.length} loans`}
                          options={loans.map(l => ({ value:l.key, label:l.label }))} />
          )}
          {cats.length > 1 && (
            <MultiFilter label="Component" value={cat} onChange={setCat}
                         allLabel="All components"
                         options={cats.map(c => ({ value:c.key, label:c.label }))} />
          )}
          <Btn onClick={csv} disabled={!flows || !flows.length}>Export CSV</Btn>
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </div>

      {busy && <Spinner />}

      {!busy && flows && !flows.length && (
        <div style={{ padding:"28px 16px", textAlign:"center", color:INK_MUTED, fontSize:13 }}>
          {sel ? "This run stored no flows for that loan."
               : "This run stored no flows for this account."}
        </div>
      )}

      {/* Rows exist, the filter matched none of them. Says which filter, so the
          reader is not left deciding whether the run is empty. */}
      {!busy && flows && !!flows.length && !shown.length && (
        <div style={{ padding:"28px 16px", textAlign:"center", color:INK_MUTED, fontSize:13 }}>
          No {keptCats.map(c => c.label.toLowerCase()).join(" or ") || "matching"} flows
          {sel ? " on that loan" : " on this account"}.
        </div>
      )}

      {!busy && !!months.length && (
        <>
          {/* NO NOTE WHEN NOTHING IS FILTERED. The two legs invest different
              money by construction — the original leg cannot see a draw taken
              two years later — so "they first differ in Apr 2024" was reporting
              the normal case as if it were a finding.

              A filtered view does need a line, because the totals below stop
              being the account's and become one slice of it. */}
          {!!(cat && cat.length) && (
            <div style={{ padding:"0 16px 10px", fontSize:12, color:INK_MUTED,
                          lineHeight:1.6 }}>
              Showing <strong style={{ color:INK_PRIMARY }}>
                {keptCats.length ? keptCats.map(c => c.label).join(", ") : "nothing"}
              </strong> only
              {" "}— {shown.length} of {byLoan.length} flows. The totals below are for
              {keptCats.length === 1 ? " this component" : " these components"}, not the
              IRR's series.
            </div>
          )}
          <div style={{ overflowX:"auto", maxHeight:"56vh" }}>
            <table style={{ borderCollapse:"collapse", width:"100%", fontSize:12,
                            fontVariantNumeric:"tabular-nums" }}>
              <thead>
                <tr>
                  <Hd sticky wide>Month</Hd>
                  <Hd sticky num help="The schedule as booked.">Original</Hd>
                  <Hd sticky num help="Actuals where they exist, otherwise the remaining schedule.">Current</Hd>
                  <Hd sticky num bal>Difference</Hd>
                  <Hd sticky num help="Running total of the difference, from the first flow.">Cumulative</Hd>
                </tr>
              </thead>
              <tbody>
                {months.map(g => (
                  <Fragment key={g.ym}>
                    <tr style={{ borderTop:"1px solid " + HAIRLINE,
                                 background: Math.abs(g.diff) > 0.005 ? SURFACE_MUTED : undefined }}>
                      <td style={{ padding:"5px 10px", whiteSpace:"nowrap" }}>
                        <button onClick={() => setOpenMonth(o => ({ ...o, [g.ym]: !o[g.ym] }))}
                                style={{ background:"none", border:"none", padding:0,
                                         cursor:"pointer", font:"inherit", color:INK_PRIMARY,
                                         display:"inline-flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:9, color:INK_MUTED, width:12,
                                         display:"inline-block" }}>
                            {openMonth[g.ym] ? "▼" : "▶"}
                          </span>
                          {monthLabel(g.ym)}
                        </button>
                      </td>
                      <Nu>{money0(g.original)}</Nu>
                      <Nu>{money0(g.current)}</Nu>
                      <Nu strong={Math.abs(g.diff) > 0.005}>
                        {Math.abs(g.diff) > 0.005 ? money0(g.diff) : "—"}</Nu>
                      <Nu dim>{Math.abs(g.cum) > 0.005 ? money0(g.cum) : "—"}</Nu>
                    </tr>
                    {/* Individual flows sit under the two leg columns they
                        belong to, so the month row above is visibly the sum of
                        what is beneath it. The source row is named in the
                        label rather than in the Difference and Cumulative
                        columns — putting it there would make two columns mean
                        one thing on parent rows and another on children. */}
                    {openMonth[g.ym] && g.rows.map((f, i) => (
                      <tr key={i} style={{ borderTop:"1px solid " + HAIRLINE, background:SURFACE }}>
                        <td style={{ padding:"4px 10px 4px 34px", whiteSpace:"nowrap",
                                     color:INK_MUTED }}>
                          {fmtDate(f.flowdate)}{" · "}
                          {!sel && loans.length > 1 && (f.loannumber || f.loanid)
                            ? (f.loannumber || f.loanid) + " · " : ""}
                          {f.flowtype}
                          {f.basis === "projected" ? " (projected)" : ""}
                          {f.sourcetable
                            ? ` · ${f.sourcetable}${f.sourceid ? " #" + f.sourceid : ""}`
                            : ""}
                        </td>
                        <Nu dim>{f.leg === "original" ? money0(num(f.amount)) : ""}</Nu>
                        <Nu dim>{f.leg === "current"  ? money0(num(f.amount)) : ""}</Nu>
                        <Nu dim />
                        <Nu dim />
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop:"2px solid " + HAIRLINE_FIRM, fontWeight:700,
                             background:SURFACE_MUTED }}>
                  <td style={{ padding:"6px 10px" }}>Total</td>
                  <Nu>{money0(tot.original)}</Nu>
                  <Nu>{money0(tot.current)}</Nu>
                  <Nu>{money0(tot.current - tot.original)}</Nu>
                  <Nu />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </Card>
  );
};

// ── small pieces ───────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <div style={{ fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase",
                letterSpacing:"0.06em", marginBottom:10 }}>{children}</div>
);

const TONE_FG = { up:"#0F6E56", down:"#993C1D", warn:B.amberDark };

// ── SelectionRow ───────────────────────────────────────────────────────────
//
// The pooled Original and Current IRR for whatever the filters currently leave,
// solved server-side from this run's frozen flows.
//
// EVERYTHING ABOUT THIS ROW IS DESIGNED TO STOP IT BEING QUOTED AS THE NUMBER
// OF RECORD. It is banded rather than carded, so it does not look like the Stat
// tiles above it; it names the pool it describes in the same breath as the
// rate; and it says in words that nobody published it. The rate is real and
// reproducible — same run, same loans, same answer tomorrow — but it belongs to
// a pool that exists only while these filters are set.
//
// A rate that will not solve shows the solver's own sentence. It never shows a
// dash that reads as zero, and it never shows a stale number from the previous
// selection while a new one is loading.
const SelectionRow = ({ busy, err, d, loans, accounts, status }) => {
  const o = d && d.original, c = d && d.current;
  const diff = (o && o.solved && c && c.solved) ? c.rate - o.rate : null;

  const cell = (label, body, note) => (
    <div style={{ minWidth:130 }}>
      <div style={{ fontSize:10.5, color:INK_MUTED, textTransform:"uppercase",
                    letterSpacing:"0.05em", marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:17, fontWeight:600,
                    fontVariantNumeric:"tabular-nums" }}>{body}</div>
      {note && <div style={{ fontSize:11, color:INK_MUTED, marginTop:2 }}>{note}</div>}
    </div>
  );

  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:20, alignItems:"flex-start",
                  padding:"12px 16px", borderRadius:8, background:SURFACE_MUTED,
                  border:"1px dashed " + HAIRLINE_FIRM }}>
      <div style={{ minWidth:150 }}>
        <div style={{ fontSize:11, fontWeight:700, color:INK_PRIMARY,
                      textTransform:"uppercase", letterSpacing:"0.06em" }}>
          Selection
        </div>
        <div style={{ fontSize:11, color:INK_MUTED, marginTop:3, lineHeight:1.5 }}>
          {loans} loan{loans === 1 ? "" : "s"} · {accounts} account{accounts === 1 ? "" : "s"},
          {" "}pooled and solved from this {status} run&apos;s frozen flows.
          {" "}Not published, and not the number of record.
        </div>
      </div>

      {busy && <div style={{ fontSize:13, color:INK_MUTED, alignSelf:"center" }}>Solving…</div>}

      {!busy && err && (
        <div style={{ fontSize:12, color:TONE_FG.down, alignSelf:"center" }}>
          Could not solve the selection: {err}
        </div>
      )}

      {!busy && !err && d && (
        <>
          {cell("Original IRR",
                o && o.solved ? pct(o.rate) : "—",
                o && !o.solved ? (o.reason || "no rate") : "the schedule as booked")}
          {cell("Current IRR",
                c && c.solved ? pct(c.rate) : "—",
                c && !c.solved ? (c.reason || "no rate") : "actuals, then remaining schedule")}
          {cell("Divergence",
                diff === null ? "—" : bps(diff) + " bps",
                diff === null ? "one leg did not solve"
                  : diff >= 0 ? "expected to return more than booked"
                              : "expected to return less than booked")}
          {cell("Money Multiple",
                c ? mult(c.moneymultiple) : "—",
                c ? money0(c.net) + " net" : null)}
          {/* Loans the run has no flows for. A selection of forty that solved on
              thirty-eight, unremarked, is the quiet kind of wrong. */}
          {d.missing && d.missing.length > 0 && cell(
            "Not in run", String(d.missing.length),
            "selected but this run has no flows for them")}
          {/* Both legs pool many loans, so more than one sign change is normal
              and not worth flagging; a leg that solved with none is the one
              worth a second look. */}
          {c && c.solved && c.horizondays !== null && c.horizondays < 90 && cell(
            "Horizon", `${c.horizondays} days`,
            "too short to annualise meaningfully")}
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, note, tone }) => (
  <Card style={{ padding:"12px 14px" }}>
    <div style={{ fontSize:11, color:INK_MUTED, textTransform:"uppercase",
                  letterSpacing:"0.05em", marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:19, fontWeight:600, fontVariantNumeric:"tabular-nums",
                  color: (tone && TONE_FG[tone]) || INK_PRIMARY }}>{value}</div>
    {note && <div style={{ fontSize:11, color:INK_MUTED, marginTop:3 }}>{note}</div>}
  </Card>
);

const Hd = ({ children, num: isNum, bal, help, sticky, wide, sort, active, dir, onSort }) => (
  <th title={help}
      onClick={sort && onSort ? () => onSort(sort) : undefined}
      style={{ textAlign:isNum ? "right" : "left", padding:"8px 10px", whiteSpace:"nowrap",
               fontSize:10.5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.04em",
               color:INK_PRIMARY, background: bal ? B.cyanLight : SURFACE,
               borderBottom:"1.5px solid " + HAIRLINE_FIRM,
               minWidth: wide ? 210 : undefined,
               position:sticky ? "sticky" : undefined, top:sticky ? 0 : undefined,
               zIndex:sticky ? 1 : undefined,
               userSelect: sort ? "none" : undefined,
               cursor: sort ? "pointer" : help ? "help" : undefined }}>
    {children}{help && <span style={{ color:INK_MUTED, fontWeight:400 }}> {"ⓘ"}</span>}
    {/* The arrow marks the ACTIVE column only. A caret on every header is
        noise, and one on a column that is not sorting anything is a lie. */}
    {sort && active && (
      <span style={{ color:B.cyanDark, marginLeft:4 }}>{dir === "asc" ? "▲" : "▼"}</span>
    )}
  </th>
);

const Nu = ({ children, strong, dim }) => (
  <td style={{ padding:"5px 10px", textAlign:"right", whiteSpace:"nowrap",
               fontWeight: strong ? 600 : undefined,
               color: dim ? INK_MUTED : INK_PRIMARY }}>{children}</td>
);

// A text button. Used for the per-note delete, which should read as a small
// word beside the note rather than a control competing with "Add note".
const linkish = { border:"none", background:"transparent", cursor:"pointer",
                  fontSize:11, padding:0 };

// Enough of a note to tell it from the one above it, read aloud without the
// listener waiting out a paragraph. Newlines collapse because a screen reader
// treats them as pauses in the middle of the button's name.
const snip = (t, n = 48) => {
  const s = String(t || "").replace(/\s+/g, " ").trim();
  return s.length <= n ? s : s.slice(0, n).replace(/\s+\S*$/, "") + "…";
};

// ── THE NOTE EDITOR ────────────────────────────────────────────────────────
//
// Opens from the notes cell on a loan row, for administrators only. Deliberately
// plain: a textarea, a save, and a delete for each note already there.
//
// THE SERVER IS WHAT ENFORCES THIS. The pencil is hidden from everyone else
// because offering a control that will 403 is unkind, not because hiding it
// protects anything — saveNote refuses a "Performance" note from a non-admin
// whether or not this component rendered.
const NoteEditor = ({ api, row, who, onClose, onSaved }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");
  const [confirm, setConfirm] = useState(null);   // id of the note asking twice

  // DELIBERATELY NOT window.confirm. A native dialog blocks the whole page, and
  // "are you sure" answered by reflex is not a second thought. The row asks in
  // place instead, and the destructive word only appears on the second press.
  const remove = async (id) => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await api.post("deleteRecord", { table: "Notes", id });
      setConfirm(null);
      await onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const add = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setErr("");
    try {
      await api.post("saveNote", {
        entitytype: "Loan", entityid: row.loanid,
        loanid: row.loanid, accountid: row.accountid,
        source: "Performance", note: t,
        notedate: new Date().toISOString().slice(0, 10),
        createdby: who || null,
      });
      setText("");
      await onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <Modal title={`Notes — ${row.companyname}${row.loannumber ? " · " + row.loannumber : ""}`}
           onClose={onClose}>
      <div style={{ fontSize:13, color:INK_MUTED, marginBottom:12 }}>
        Shown to everyone who can open this page, on this loan, in every run from
        now on — not just this one.
      </div>

      {err ? <ErrorMsg msg={err} /> : null}

      {row.noteRows.length ? (
        <div style={{ marginBottom:14 }}>
          {row.noteRows.map(n => (
            <div key={n.id} style={{ borderTop:"1px solid " + HAIRLINE, padding:"8px 0",
                                     display:"flex", gap:10, alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, color:INK_PRIMARY, whiteSpace:"pre-wrap" }}>{n.note}</div>
                <div style={{ fontSize:11, color:INK_MUTED, marginTop:2 }}>
                  {n.createdby || "unknown"}
                  {n.createdat ? " · " + fmtDate(n.createdat) : ""}
                </div>
              </div>
              {confirm === n.id ? (
                <div style={{ display:"flex", gap:8, flex:"0 0 auto", alignItems:"center" }}>
                  <button type="button" onClick={() => remove(n.id)} disabled={busy}
                          style={{ ...linkish, color:TONE_FG.down, fontWeight:600 }}>
                    {busy ? "Deleting…" : "Delete it"}</button>
                  <button type="button" onClick={() => setConfirm(null)}
                          style={{ ...linkish, color:INK_MUTED }}>Keep</button>
                </div>
              ) : (
                // THE LABEL HAS TO NAME *THIS* NOTE. Labelling it by author put
                // the same words on every button, because one admin writes most
                // of them — three identical "Delete the note written by …" in a
                // row is no more use than three bare "Delete"s. The note's own
                // opening words are what tell them apart.
                <button type="button" onClick={() => { setConfirm(n.id); setErr(""); }}
                        aria-label={`Delete the note “${snip(n.note)}”`}
                        style={{ ...linkish, color:INK_MUTED, flex:"0 0 auto" }}>Delete</button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize:13, color:INK_MUTED, marginBottom:14 }}>
          Nothing written against this loan yet.
        </div>
      )}

      <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
                placeholder="Why this loan reads the way it does…"
                style={{ width:"100%", boxSizing:"border-box", padding:"8px 10px",
                         fontSize:13, borderRadius:6, border:"1px solid " + HAIRLINE_FIRM,
                         fontFamily:"inherit", resize:"vertical" }} />
      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:10 }}>
        <Btn onClick={onClose}>Close</Btn>
        <Btn variant="primary" onClick={add} disabled={busy || !text.trim()}>
          {busy ? "Saving…" : "Add note"}</Btn>
      </div>
    </Modal>
  );
};

// One row of the ranking, account or loan. `cols` is the figure-bearing record
// (they are the same shape at both levels, which is why the table can be one
// component rather than two that drift).
const PerfRow = ({ cols, name, after, bold, child, extraNote, onNote }) => {
  const c = cols || {};
  const notes = notesOf(c, extraNote);

  return (
    <tr style={{ borderTop:"1px solid " + HAIRLINE,
                 background: child ? SURFACE_MUTED : undefined,
                 fontWeight: bold ? 700 : 400 }}>
      <td style={{ padding:"5px 10px", paddingLeft: child ? 32 : 10, whiteSpace:"nowrap",
                   color: child ? INK_MUTED : INK_PRIMARY }}>{name}{after}</td>
      <td style={{ padding:"5px 10px", color:INK_MUTED }}>{c.facilitytype || "—"}</td>
      <td style={{ padding:"5px 10px", color:INK_MUTED }}>{c.collateraltype || "—"}</td>
      <td style={{ padding:"5px 10px", color:INK_MUTED }}>{c.state || "—"}</td>
      <td style={{ padding:"5px 10px", color:INK_MUTED, whiteSpace:"nowrap" }}>
        {c.maturitydate ? fmtDate(c.maturitydate) : "—"}</td>
      <Nu>{pct(c.origRate)}</Nu>
      <Nu>{pct(c.currRate)}</Nu>
      <td style={{ padding:"5px 10px", textAlign:"right", whiteSpace:"nowrap",
                   background:B.cyanLight, fontWeight:600,
                   color: c.diff === null || c.diff === undefined ? INK_MUTED
                          : c.diff >= 0 ? TONE_FG.up : TONE_FG.down }}>
        {c.diff === null || c.diff === undefined ? "—" : bps(c.diff)}</td>
      <Nu dim>{mult(c.moneymultiple)}</Nu>
      <Nu dim>{c.netcash === undefined ? "—" : money0(c.netcash)}</Nu>
      <Nu dim>{c.flowcount || "—"}</Nu>
      <td style={{ padding:"5px 10px", fontSize:11, color:INK_MUTED, maxWidth:260 }}>
        <div style={{ display:"flex", gap:6, alignItems:"baseline" }}>
          <div style={{ flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap" }} title={notes.join(" · ")}>
            {notes.join(" · ")}
          </div>
          {onNote ? (() => {
            // "✎" is not a name. Without aria-label the button announces as the
            // pencil character or as nothing at all, and every row's announces
            // identically, so the loan goes in the label too.
            //
            // NOT `name` — that prop is a JSX <button>, and interpolating it
            // yields "[object Object]". The loan number is on `cols`, which is
            // the only row shape that ever gets a pencil.
            const who = c.loannumber || c.loanid || "this loan";
            const label = c.noteRows && c.noteRows.length
              ? `Notes on loan ${who} — ${c.noteRows.length} note${c.noteRows.length === 1 ? "" : "s"}`
              : `Add a note to loan ${who}`;
            return (
              <button type="button" onClick={onNote} title={label} aria-label={label}
                      style={{ border:"none", background:"transparent", cursor:"pointer",
                               color:B.cyan, fontSize:11, padding:0, flex:"0 0 auto" }}>
                {c.noteRows && c.noteRows.length ? "note ✎" : "✎"}
              </button>
            );
          })() : null}
        </div>
      </td>
    </tr>
  );
};
